import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type MutableRefObject,
    type RefObject,
} from "react";

import type { MessageDateGroup } from "./group-messages-by-date";

interface UseMessageGroupCollapseParams {
    groups: MessageDateGroup[];
    messageCount: number;
    scrollRef: RefObject<HTMLDivElement | null>;
    isStickyBottomRef: MutableRefObject<boolean>;
    // Парент может пробросить ref, в который публикуется handleNavScroll — это
    // даёт ChatHeader (in-chat search) скроллить к message_id с auto-expand
    // свёрнутых дней, не дублируя логику.
    scrollToMessageRef?: MutableRefObject<((messageId: string) => void) | null>;
}

// Свёрнутые date-группы списка: состояние collapse, детерминированный
// collapsedKey (ключ для пересоздания IntersectionObserver в MessageScrollNav),
// auto-разворот последней группы при новом сообщении и handleNavScroll
// (auto-expand дня + scrollIntoView), публикуемый в scrollToMessageRef.
export function useMessageGroupCollapse({
    groups,
    messageCount,
    scrollRef,
    isStickyBottomRef,
    scrollToMessageRef,
}: UseMessageGroupCollapseParams) {
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
    // Меняется ровно когда юзер toggle'нул хоть один день. MessageScrollNav
    // пересоздаёт IntersectionObserver на этом ключе, чтобы наблюдать
    // актуальные [data-msg-anchor]-узлы.
    const collapsedKey = useMemo(
        () =>
            Object.entries(collapsed)
                .filter(([, v]) => v)
                .map(([k]) => k)
                .sort()
                .join("|"),
        [collapsed],
    );

    const toggleGroup = useCallback((dateKey: string) => {
        setCollapsed((prev) => ({ ...prev, [dateKey]: !prev[dateKey] }));
    }, []);

    // Авто-разворот последней группы («сегодня») при новом сообщении — иначе
    // юзер свернул «Today» → отправил промт → не видит свой свежий пузырь.
    // prevCountRef защищает от срабатывания на первом монтировании / загрузке.
    const prevCountRef = useRef(messageCount);
    useEffect(() => {
        if (messageCount > prevCountRef.current && groups.length > 0) {
            const last = groups[groups.length - 1]!;
            setCollapsed((prev) => (prev[last.dateKey] ? { ...prev, [last.dateKey]: false } : prev));
        }
        prevCountRef.current = messageCount;
    }, [messageCount, groups]);

    const handleNavScroll = useCallback(
        (messageId: string) => {
            // Юзер явно уезжает в середину чата (клик в нав-полоске или результат
            // in-chat search). Выходим из sticky-bottom, иначе reanchorIfSticky
            // после layout-shift вернёт viewport к низу.
            isStickyBottomRef.current = false;
            const group = groups.find((g) => g.messages.some((m) => m.id === messageId));
            const scrollTo = () => {
                const target = scrollRef.current?.querySelector<HTMLElement>(
                    `[data-msg-anchor="${messageId}"]`,
                );
                target?.scrollIntoView({ behavior: "smooth", block: "center" });
            };
            if (group && collapsed[group.dateKey]) {
                setCollapsed((prev) => ({ ...prev, [group.dateKey]: false }));
                // Двойной rAF: первый — после commit collapsed state, второй —
                // после реального layout развёрнутых сообщений. Без этого
                // scrollIntoView ловит ещё свёрнутый DOM и промахивается.
                requestAnimationFrame(() => requestAnimationFrame(scrollTo));
            } else {
                scrollTo();
            }
        },
        [groups, collapsed, scrollRef, isStickyBottomRef],
    );

    // Публикуем handleNavScroll в parent-ref для ChatHeader (in-chat search).
    useEffect(() => {
        if (!scrollToMessageRef) return;
        scrollToMessageRef.current = handleNavScroll;
        return () => {
            scrollToMessageRef.current = null;
        };
    }, [scrollToMessageRef, handleNavScroll]);

    return { collapsed, toggleGroup, collapsedKey, handleNavScroll };
}
